import { ClientSchema, ClientSchemaElementType } from "src/types/clientSchema";

import { WidgetDefinition, WidgetPropertyDefinition } from "../Widget.typings";

import { isCodeEqual } from "./widgetProperties.utils";

// Resolve element type for a dotted path (e.g. "RepresentantLegal.ControleTelephoneLucy"):
// traverse elementTypes -> elements -> elementType until the last segment, then return that type's properties.
const resolveElementTypeByPath = (
  caseSchema: ClientSchema | undefined | null,
  elementPath: string
): ClientSchemaElementType | undefined => {
  if (!caseSchema) return undefined;
  const segments = elementPath.split(".");
  const rootCode = segments[0];
  if (!rootCode) return undefined;

  let currentType = caseSchema.elementTypes.find((et) => et.code === rootCode);
  if (!currentType) return undefined;
  if (segments.length === 1) return currentType;

  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];
    // Match by schema element code first (e.g. lucy_telephone, lucy_telephone_formulaire)
    let child = currentType.elements?.find((el) => el.code === segment);
    // Fallback: match by elementType code when schema uses one type for multiple variants (e.g. ControleTelephoneLucy)
    if (!child) {
      child = currentType.elements?.find((el) => el.elementType.code === segment);
    }
    if (!child) return undefined;
    currentType = caseSchema.elementTypes.find((et) => et.code === child!.elementType.code);
    if (!currentType) return undefined;
  }
  return currentType;
};

// Get properties from client schema based on elementCode and caseTypeCode
// If elementCode is provided, get properties from element type (supports dotted path into nested elements)
// If elementCode is not provided, get properties from case type
export const getPropertiesFromClientSchema = (
  elementCode: string | null | undefined,
  caseTypeCode: string | null | undefined,
  caseSchema: ClientSchema | undefined | null
): WidgetPropertyDefinition[] => {
  if (!elementCode) {
    // Get properties from case type
    const caseType = caseSchema?.caseTypes.find((ct) => ct.code === caseTypeCode);
    return (
      caseType?.properties.map<WidgetPropertyDefinition>((property) => ({
        code: property.code,
        property: property.code,
        label: property.name,
      })) ?? []
    );
  }
  // Get properties from element type (supports dotted path e.g. RepresentantLegal.ControleTelephoneLucy)
  const elementType = resolveElementTypeByPath(caseSchema, elementCode);
  return (
    elementType?.properties.map<WidgetPropertyDefinition>((property) => ({
      code: property.code,
      property: property.code,
      label: property.name,
    })) ?? []
  );
};

export const getDisplayedPropertiesFromClientSchema = (
  widgetDefinition: WidgetDefinition | undefined | null,
  caseType: string,
  caseSchema: ClientSchema | undefined | null
): WidgetPropertyDefinition[] => {
  if (!widgetDefinition || !caseSchema) {
    return [];
  }

  const displayedProperties = widgetDefinition.configuration?.displayedProperties ?? [];

  return displayedProperties.map((property) => {
    // Return if label is already defined
    if (property.label) {
      return property;
    }

    // TODO: should use elementCode instead of elementPath when all widget are migrated
    const elementCode =
      property.elementCode ?? property.elementPath ?? widgetDefinition?.configuration?.elementPath;

    // If no elementCode get from case type
    if (!elementCode) {
      const schemaCaseType = caseSchema.caseTypes.find((ct) => ct.code === caseType);
      return getPropertyWithLabel(property, schemaCaseType?.properties);
    }

    // Otherwise get from element type (supports dotted path into nested elements)
    const elementType = resolveElementTypeByPath(caseSchema, elementCode);
    const elementProperties = elementType?.properties ?? [];
    return getPropertyWithLabel(property, elementProperties);
  });
};

// enrich property with label from schema
const getPropertyWithLabel = (
  property: WidgetPropertyDefinition,
  schemaProperties?: { code: string; name: string }[]
): WidgetPropertyDefinition => {
  // Return if label is already defined
  if (property.label) {
    return property;
  }

  // Handle single property case
  if (property.property) {
    const propertyFromSchema = schemaProperties?.find((p) =>
      isCodeEqual(p.code, property.property!)
    );
    return {
      ...property,
      label: propertyFromSchema?.name,
    };
  }

  // Handle multiple properties case
  if (property.properties) {
    const propertyNames = property.properties
      .map((propertyCode) => {
        const propertyFromSchema = schemaProperties?.find((p) => isCodeEqual(p.code, propertyCode));
        return propertyFromSchema?.name;
      })
      .filter((name): name is string => !!name);

    return {
      ...property,
      label: propertyNames.join("/"),
    };
  }

  return property;
};
